import { alert } from 'common/components';
import { bytesToText } from 'common/utils';
import { PDFDocument } from 'pdf-lib';
import React from 'react';
import Dropzone from 'react-dropzone';
import { ArquivoService } from 'service';

import DragDropItem from './DragDropItem';

const DEFAULT_MAX_SIZE = 20971520;

export const checkPdfSignatures = async file => {
  file.hasSignatures = false;
  if (!file.name?.toLowerCase().endsWith('.pdf')) {
    return;
  }

  const arrayBuffer = await file.arrayBuffer();
  let document;
  try {
    document = await PDFDocument.load(arrayBuffer, {
      ignoreEncryption: true
    });
    const acroForm = document.catalog?.getAcroForm();
    const acroFields = acroForm?.getFields();

    file.hasSignatures = !!acroFields?.some(([field]) => {
      // A maioria das assinaturas (Gov.BR, nossas, etc) usam Fields específicos para assinatura.
      if (field?.constructor.name.toLowerCase().includes('signature')) {
        return true;
      }

      /* Necessário pois algumas assinaturas como as do DocuSign,
        usam um Field de outro tipo (Widget, por exemplo) para
        encapsular uma assinatura. Então precisamos ver se o
        Field contém metadados de assinatura. */
      const dictValues = field?.dict?.dict?.values?.() || [];
      for (const dictValue of dictValues) {
        if (dictValue?.encodedName === '/Sig') {
          return true;
        }
      }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      'Erro ao verificar se PDF possui assinaturas. Considerando que não possui.',
      file,
      e
    );
    file.hasSignatures = false;
    return;
  }
};

export default class DragDropFiles extends React.Component {
  onDrop = acceptedFilesRaw => {
    const times = [];
    const acceptedFiles = acceptedFilesRaw.map(file => {
      const sanitizedName = file.name.replace(/[^\x20-\x7E\xA0-\xFF]/g, '');

      if (sanitizedName === file.name) {
        return file;
      }

      return new File([file], sanitizedName, {
        type: file.type,
        lastModified: file.lastModified
      });
    });
    const filesPromises = acceptedFiles.map(file => checkPdfSignatures(file));

    Promise.all(filesPromises).then(() =>
      acceptedFiles.forEach(file => {
        times.push(
          setTimeout(() => {
            this.props.addFile(file);
          }, 200)
        );
      })
    );

    return () => times.forEach(time => clearTimeout(time));
  };

  onDropRejected = tamanhoArquivo => () => {
    const tamanhoFormatado = bytesToText(tamanhoArquivo);
    const config = {
      title: 'Informação',
      text: `Não é permitido arquivo maior do que ${tamanhoFormatado}.`,
      type: 'info',
      confirmButtonText: 'Fechar'
    };

    alert(config);
  };

  renderAlertaExclusao = (arquivo, index) => {
    if (!this.props.showAlertExclusao) {
      return this.excluirArquivo(arquivo, index);
    }

    alert({
      type: 'warning',
      title: 'Exclusão',
      text: 'Deseja mesmo excluir o arquivo?',
      cancelButtonColor: '#c7382d',
      cancelButtonText: 'Cancelar',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      onConfirm: () => this.excluirArquivo(arquivo, index)
    });
  };

  renderAlertaRejeicao = (arquivo, index) => {
    alert({
      type: 'warning',
      title: 'Rejeitar',
      input: 'textarea',
      showCancelButton: true,
      cancelButtonColor: '#c7382d',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Rejeitar',
      onConfirm: motivo => this.rejeitarArquivo(arquivo, index, motivo),
      text: 'Deseja mesmo rejeitar o arquivo?',
      inputPlaceholder: 'Informe o motivo da rejeição',
      inputValidator: value => {
        return new Promise(resolve => {
          if (!value) {
            return resolve('Informe o motivo de estar recusando o arquivo.');
          }

          resolve(false);
        });
      }
    });
  };

  renderAlertaAprovacao = (arquivo, index) => {
    alert({
      type: 'warning',
      title: 'Aprovar',
      showCancelButton: true,
      cancelButtonColor: '#c7382d',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Aprovar',
      onConfirm: () => this.aprovarArquivo(arquivo, index),
      text: 'Deseja mesmo aprovar o arquivo?'
    });
  };

  excluirArquivo = (data, index) => {
    this.props.removeFile(data, index);
  };

  rejeitarArquivo = async (arquivo, index, motivo) => {
    const { analisarArquivo, replaceFile } = this.props;

    const analise = {
      motivo,
      analise: 'R',
      identificador: arquivo.identificador
    };

    const res = await analisarArquivo(analise);
    replaceFile && replaceFile(res.value, index);
  };

  renameFile = async (arquivo, index) => {
    const { replaceFile } = this.props;

    alert({
      title: 'Renomear arquivo',
      input: 'text',
      inputValue: arquivo.nome,
      type: 'info',
      text: 'Renomear nomenclatura do arquivo',
      inputPlaceholder: 'Digite o novo nome do arquivo',
      showCancelButton: true,
      confirmButtonText: 'Renomear',
      cancelButtonText: 'Cancelar',
      onConfirm: async nome => {
        await ArquivoService.editarNomeArquivo({
          identificador: arquivo.identificador,
          nome
        }).then(novoArquivo => replaceFile && replaceFile(novoArquivo, index));
      },
      inputValidator: value => {
        return new Promise(resolve => {
          if (value) {
            resolve();
          } else {
            resolve('Digite o nome.');
          }
        });
      }
    });
  };

  aprovarArquivo = async (arquivo, index) => {
    const { analisarArquivo, replaceFile } = this.props;

    const analise = {
      analise: 'A',
      identificador: arquivo.identificador
    };

    const res = await analisarArquivo(analise);
    replaceFile && replaceFile(res.value, index);
  };

  renderItens = (item, index) => {
    const {
      downloadFile,
      printFile,
      readOnly,
      previewFile,
      canDelete,
      canDownload,
      canApprove,
      canReject,
      canRename,
      analisarArquivo
    } = this.props;

    return (
      <DragDropItem
        item={item}
        index={index}
        readOnly={readOnly}
        canDelete={canDelete}
        onPreview={previewFile}
        canDownload={canDownload}
        canReject={item.analise === 'N' && canReject && analisarArquivo}
        canApprove={item.analise === 'N' && canApprove && analisarArquivo}
        canRename={canRename && !readOnly}
        onRenameFile={this.renameFile}
        onDownload={downloadFile}
        onPrintFile={printFile}
        onRemove={this.renderAlertaExclusao}
        onReject={this.renderAlertaRejeicao}
        onApprove={this.renderAlertaAprovacao}
        key={`${item.id}-${item.nome}-${index}`}
      />
    );
  };

  render() {
    const {
      files,
      readOnly,
      canAdd,
      maxSize = DEFAULT_MAX_SIZE,
      style
    } = this.props;

    return (
      <section>
        <div className="dragdrop" style={style}>
          {!readOnly && canAdd && (
            <Dropzone
              onDrop={this.onDrop}
              onDropRejected={this.onDropRejected(maxSize)}
              maxSize={maxSize}
              data-message={`Clique aqui ou arraste os arquivos para esta área. Tamanho máximo por arquivo: ${bytesToText(
                maxSize
              )}`}
            >
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps?.()}
                  className="dragdrop-space"
                  data-message={`Clique aqui ou arraste os arquivos para esta área. Tamanho máximo por arquivo: ${bytesToText(
                    maxSize
                  )}`}
                >
                  <input {...getInputProps?.()} />
                </div>
              )}
            </Dropzone>
          )}
          {!!files?.length && <ul>{files.map(this.renderItens)}</ul>}
        </div>
      </section>
    );
  }
}

DragDropFiles.defaultProps = {
  canDownload: true,
  canReject: true,
  canApprove: true,
  showAlertExclusao: true,
  canRename: false
};
